// Copyright (c) Said Borna. All rights reserved.
import { type ReactElement } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ImpersonationProvider } from '@/contexts/ImpersonationContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { router } from '@/router';

const STALE_TIME_MS = 2 * 60 * 1000;

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: STALE_TIME_MS,
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

/**
 * Root application component.
 * Provides query client, auth, impersonation contexts, and top-level error boundary.
 */
export default function App(): ReactElement {
    return (
        <ErrorBoundary context="Application">
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <ImpersonationProvider>
                        <RouterProvider router={router} />
                    </ImpersonationProvider>
                </AuthProvider>
            </QueryClientProvider>
        </ErrorBoundary>
    );
}
